import React, { useEffect } from "react";
import { useForm, FormProvider, FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardHeader, CardBody, CardFooter, Button, Divider } from "@heroui/react";
import { Icon } from "@iconify/react";
import { PersonalInfoForm } from "../../admin/AdminCreateRequest/PersonalInfoForm";
import { MedicalQuestionsForm } from "../../admin/AdminCreateRequest/MedicalQuestionsForm";
import { DosageForm } from "../../admin/AdminCreateRequest/DosageForm";
import { DocumentsUploadForm } from "../../admin/AdminCreateRequest/DocumentsUploadForm";
import { TransportersForm } from "../../admin/AdminCreateRequest/TransportersForm";
import { RequestSchema, TRequest } from "@shared/entities";
import { useAppApi } from "src/appApi";
import { useParams } from "wouter";

type Step = {
	fields: Array<FieldPath<TRequest>>;
};
const steps: Array<Step> = [
	{ fields: ["clientDetails", "requestType", "userComment"] },
	{
		fields: ["medicalQuestionnaire"],
	},
	{
		fields: ["dosages", "existsDosage"],
	},
	{
		fields: ["documents"],
	},
	{
		fields: ["transporters"],
	},
] as const;

export default function BrokerEditRequest() {
	const params = useParams();
	const requestId = params.id;

	const methods = useForm<TRequest>({
		resolver: zodResolver(RequestSchema),
		shouldUnregister: false,
		defaultValues: {
			paymentDetails: {
				status: "pending",
				cost: 0,
				payments: [],
			},
		},
	});

	const appApi = useAppApi();

	const {
		handleSubmit,
		formState: { isSubmitting },
	} = methods;

	const onSubmit = async (data: TRequest) => {
		try {
			// If status is change_required_broker, set it to pending when broker edits
			const requestData = {
				...data,
				status: data.status === "change_required_broker" ? "pending" : data.status,
			};
			await appApi.api.broker.setRequest(requestData);
			appApi.navigate("/broker");
		} catch (error) {
			console.error("Error submitting form:", error);
		}
	};

	const [currentStep, setCurrentStep] = React.useState(1);
	const totalSteps = steps.length;

	const isLastStep = currentStep >= totalSteps;

	const nextStep = async () => {
		if (isLastStep) {
			handleSubmit(onSubmit, (errors) => {
				appApi.api.system.uiLogs({
					message: "form error",
					severity: "ERROR",
					errors,
				});
			})();
			return;
		}
		const stepDetails = steps[currentStep - 1];

		if (!stepDetails) return;

		const isValid = await methods.trigger(stepDetails.fields);

		if (!isValid) {
			return;
		}

		setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
	};

	const prevStep = () => {
		setCurrentStep((prev) => Math.max(prev - 1, 1));
	};

	const renderStepContent = () => {
		switch (currentStep) {
			case 1:
				return <PersonalInfoForm />;
			case 2:
				return <MedicalQuestionsForm />;
			case 3:
				return <DosageForm />;
			case 4:
				return <DocumentsUploadForm />;
			case 5:
				return <TransportersForm />;
			default:
				return <PersonalInfoForm />;
		}
	};

	const getStepTitle = () => {
		switch (currentStep) {
			case 1:
				return "פרטים אישיים";
			case 2:
				return "שאלון רפואי";
			case 3:
				return "מינון מבוקש";
			case 4:
				return "העלאת מסמכים";
			case 5:
				return "פרטי משנעים";
			default:
				return "";
		}
	};

	useEffect(() => {
		if (!requestId) return;

		appApi.api.broker.getRequest(requestId).then((res) => {
			if (res.success) {
				methods.reset({
					...res.data,
					paymentDetails: {
						status: res.data.paymentDetails?.status ?? "pending",
						cost: res.data.paymentDetails?.cost ?? 0,
						payments: res.data.paymentDetails?.payments ?? [],
					},
				});
			}
		});
	}, [requestId]);

	return (
		<div className="min-h-screen bg-gray-50 p-4 md:p-6">
			<div className="max-w-4xl mx-auto">
				<Card className="mb-6">
					<CardHeader className="flex flex-col gap-1">
						<h1 className="text-2xl font-bold">עריכת בקשה</h1>
						<p className="text-default-500">עדכון פרטי בקשה לרישיון קנאביס רפואי</p>
					</CardHeader>
				</Card>

				<div className="mb-6">
					<div className="flex justify-between items-center mb-2">
						<h2 className="text-lg font-semibold">{getStepTitle()}</h2>
						<div className="text-sm text-default-500">
							שלב {currentStep} מתוך {totalSteps}
						</div>
					</div>
					<div className="w-full bg-default-100 rounded-full h-2.5">
						<div
							className="bg-primary h-2.5 rounded-full transition-all duration-300 ease-out"
							style={{ width: `${(currentStep / totalSteps) * 100}%` }}
						></div>
					</div>
				</div>

				<FormProvider {...methods}>
					<form
						onSubmit={handleSubmit(onSubmit, (errors) => {
							appApi.api.system.uiLogs({
								message: "form error",
								severity: "ERROR",
								errors,
							});
						})}
					>
						<Card className="mb-6">
							<CardBody>{renderStepContent()}</CardBody>
							<Divider />
							<CardFooter className="flex justify-between">
								<Button
									variant="flat"
									color="default"
									startContent={<Icon icon="lucide:arrow-right" className="h-4 w-4" />}
									onPress={prevStep}
									isDisabled={currentStep === 1}
								>
									הקודם
								</Button>

								<Button
									color={isLastStep ? "success" : "primary"}
									type={"button"}
									isLoading={isSubmitting}
									onPress={nextStep}
									endContent={
										isLastStep ? (
											<Icon icon="lucide:check" className="h-4 w-4" />
										) : (
											<Icon icon="lucide:arrow-left" className="h-4 w-4" />
										)
									}
								>
									{isLastStep ? "שמירה" : "המשך"}
								</Button>
							</CardFooter>
						</Card>
					</form>
				</FormProvider>
			</div>
		</div>
	);
}
